import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, Check, Loader2, Plug, X } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "../components/ui/card";
import {
	decideAuthorization,
	describeScope,
	getPendingAuthorization,
	isDestructiveScope,
	rememberPendingAuthorization,
} from "../lib/mcp";

/**
 * Schermata di consenso del connettore MCP.
 * Spec: docs/ai-program/10-byok-and-mcp-rework.md (Fase 3).
 *
 * E' l'unico punto del flusso OAuth che vede un essere umano, quindi deve
 * dire **cosa** sta per concedere e **a chi**, non "autorizzi?". Il coach sta
 * per dare a un servizio esterno accesso ai dati dei suoi atleti: il consenso
 * informato e' il presupposto, non una formalita'.
 */
export default function McpAuthorize() {
	const [params] = useSearchParams();
	const { user, loading } = useAuth();
	const navigate = useNavigate();
	const requestId = params.get("request");

	const [pending, setPending] = useState(null);
	const [error, setError] = useState(null);
	const [deciding, setDeciding] = useState(false);
	// Dopo la decisione la pagina non resta sullo spinner: se il ritorno al
	// client non parte (scheda vecchia, client chiuso nel frattempo) il coach
	// vede cosa e' successo e ha il link per riprovare il ritorno a mano.
	const [decided, setDecided] = useState(null);
	// I permessi che il client non ha chiesto: proposti gia' spuntati, il
	// coach li toglie se non li vuole (scelta di Daniele, 2026-10-27).
	const [extras, setExtras] = useState([]);

	useEffect(() => {
		if (loading) return;
		if (!user) {
			// Il redirect arriva dal client MCP: il coach puo' non avere una
			// sessione aperta. Si parcheggia la destinazione e si torna qui.
			rememberPendingAuthorization();
			navigate("/login", { replace: true });
			return;
		}
		if (!requestId) {
			setError("Richiesta di collegamento assente o malformata.");
			return;
		}
		getPendingAuthorization(requestId)
			.then(setPending)
			.catch((err) => setError(err.message));
	}, [user, loading, requestId, navigate]);

	// Le spunte si inizializzano una volta per richiesta: se la pagina rilegge
	// la stessa richiesta (sessione rinfrescata), le scelte del coach restano.
	const pendingId = pending?.request_id;
	const pendingExtras = pending?.extra_scopes;
	useEffect(() => {
		if (pendingId) setExtras(pendingExtras || []);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pendingId]);

	const decide = async (approve) => {
		setDeciding(true);
		try {
			const { redirect_to: redirectTo } = await decideAuthorization(
				requestId,
				approve,
				approve ? extras : [],
			);
			// Si torna al client MCP, che completa lo scambio del codice.
			setDecided({ approve, redirectTo });
			window.location.href = redirectTo;
		} catch (err) {
			setError(err.message);
			setDeciding(false);
		}
	};

	// Capability, non modalita' attiva: un account doppio (atleta + coach
	// abilitato) puo' autorizzare il connettore anche mentre e' in modalita'
	// atleta. Il backend applica lo stesso criterio.
	const isCoach =
		user?.role === "coach" || !!user?.coach_enabled || user?.is_admin;
	// Cio' che il coach concede davvero: il richiesto piu' gli extra spuntati.
	const granted = [...(pending?.scopes || []), ...extras];
	const toggleExtra = (scope) =>
		setExtras((prev) =>
			prev.includes(scope)
				? prev.filter((item) => item !== scope)
				: [...prev, scope],
		);

	// La pagina scorre da se': `html`, `body` e `#root` hanno `overflow: hidden`
	// (shell PWA), quindi con `min-h-screen` su un portatile il bottone
	// «Consenti» finiva sotto il bordo dello schermo, irraggiungibile.
	return (
		<div className="fixed inset-0 overflow-y-auto overscroll-contain bg-background">
			<div className="flex min-h-full items-center justify-center p-4">
				<Card className="w-full max-w-md bg-card border-border rounded-sm">
					<CardHeader>
						<CardTitle className="font-heading text-xl flex items-center gap-2">
							<Plug className="w-5 h-5 text-volt" />
							Collega un assistente AI
						</CardTitle>
						<CardDescription>
							{pending?.client_name
								? `${pending.client_name} chiede di accedere al tuo account Performance PRO Coach.`
								: "Verifica della richiesta…"}
						</CardDescription>
					</CardHeader>

					<CardContent className="space-y-4">
						{error && (
							<div className="flex items-start gap-2 rounded-sm border border-blaze/40 bg-blaze/10 p-3 text-sm text-blaze">
								<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
								<span>{error}</span>
							</div>
						)}

						{!error && !pending && (
							<div className="flex justify-center py-6">
								<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
							</div>
						)}

						{decided && !error && (
							<div className="space-y-3 text-sm" data-testid="mcp-decided">
								<p className="flex items-start gap-2">
									<Check className="mt-0.5 h-4 w-4 shrink-0 text-volt" />
									{decided.approve
										? `Collegamento autorizzato. Stiamo tornando a ${pending?.client_name || "il client"}.`
										: "Collegamento rifiutato."}
								</p>
								<p className="text-xs text-muted-foreground">
									Se la pagina non cambia da sola,{" "}
									<a
										className="underline text-foreground"
										href={decided.redirectTo}
									>
										torna a {pending?.client_name || "il client"}
									</a>
									. Se il client nel frattempo è stato chiuso, rilancia il
									collegamento da lì: questa richiesta è già stata usata.
								</p>
							</div>
						)}

						{pending && !error && !decided && (
							<>
								{!isCoach && (
									<div className="rounded-sm border border-blaze/40 bg-blaze/10 p-3 text-sm text-blaze">
										Il connettore è riservato agli account coach.
									</div>
								)}

								<div className="space-y-2">
									<p className="text-xs uppercase tracking-wide text-muted-foreground">
										Potrà
									</p>
									<ul className="space-y-1.5">
										{pending.scopes.map((scope) => {
											// Il permesso che cancella non puo' stare nella stessa
											// riga grigia degli altri due: e' l'unico di cui il
											// coach si pentirebbe, ed e' quello su cui deve
											// fermarsi a leggere.
											const destructive = isDestructiveScope(scope);
											return (
												<li
													key={scope}
													className={`flex items-start gap-2 text-sm ${
														destructive ? "text-blaze" : ""
													}`}
												>
													{destructive ? (
														<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-blaze" />
													) : (
														<Check className="mt-0.5 h-4 w-4 shrink-0 text-volt" />
													)}
													{describeScope(scope)}
												</li>
											);
										})}
									</ul>
								</div>

								{(pending.extra_scopes || []).length > 0 && (
									<div className="space-y-2" data-testid="mcp-extra-scopes">
										<p className="text-xs uppercase tracking-wide text-muted-foreground">
											Puoi concedere anche
										</p>
										<p className="text-xs text-muted-foreground">
											{pending.client_name || "Il client"} non li ha chiesti, ma
											servono a funzioni nuove. Togli la spunta a quelli che non
											vuoi concedere.
										</p>
										<ul className="space-y-1.5">
											{pending.extra_scopes.map((scope) => {
												const destructive = isDestructiveScope(scope);
												return (
													<li key={scope}>
														<label
															className={`flex items-start gap-2 text-sm cursor-pointer ${
																destructive ? "text-blaze" : ""
															}`}
														>
															<input
																type="checkbox"
																className="mt-1 h-4 w-4 shrink-0 accent-current"
																checked={extras.includes(scope)}
																onChange={() => toggleExtra(scope)}
																aria-label={describeScope(scope)}
															/>
															<span>{describeScope(scope)}</span>
														</label>
													</li>
												);
											})}
										</ul>
									</div>
								)}

								{granted.some(isDestructiveScope) && (
									<div className="rounded-sm border border-blaze/40 bg-blaze/10 p-3 text-xs text-blaze">
										{granted.includes("programs:modify") && (
											<p>
												Questo collegamento potrà <strong>cancellare</strong>{" "}
												esercizi, giorni e settimane dai tuoi programmi. Prima
												di ogni modifica salviamo com'era il programma, e puoi
												riportarlo indietro per due giorni da Impostazioni → AI.
											</p>
										)}
										{granted.includes("models:write") && (
											<p
												className={
													granted.includes("programs:modify") ? "mt-2" : ""
												}
											>
												Potrà anche{" "}
												<strong>cambiare i tuoi modelli prestativi</strong>, con
												cui sono valutati i tuoi atleti: toglierne parti,
												pubblicarli o cancellarli. Prima di ogni modifica
												salviamo com'era il modello, e puoi riportarlo indietro
												per due giorni da Impostazioni → AI.
											</p>
										)}
										<p className="mt-2">
											Se non ti serve, autorizzalo da un client che chieda solo
											lettura e scrittura: le altre funzioni continuano a
											funzionare.
										</p>
									</div>
								)}

								<div className="rounded-sm border border-border bg-secondary/40 p-3 text-xs text-muted-foreground">
									<p>
										Vedrà solo i <strong>tuoi</strong> atleti e i tuoi
										programmi, come quando usi l'app. Le settimane che crea
										restano <strong>non pubblicate</strong>: pubblicarle resta
										una tua scelta.
									</p>
									<p className="mt-2">
										Oltre a programmi e risultati, potrà leggere{" "}
										<strong>quello che i tuoi atleti scrivono</strong>: le note
										sui risultati e i messaggi di feedback sugli allenamenti. I
										messaggi generali della chat restano fuori.
									</p>
									<p className="mt-2">
										Se un tuo atleta ha attivato il{" "}
										<strong>diario benessere</strong>, il connettore vedrà
										l'andamento medio del suo recupero — sono dati di salute, e
										escono solo per gli atleti che hanno dato il consenso in
										app. Puoi scollegarlo quando vuoi da Impostazioni.
									</p>
								</div>

								{/* Le azioni restano attaccate al fondo dello schermo mentre
							    si legge: su uno schermo basso non devono sparire. */}
								<div className="sticky bottom-0 -mx-6 -mb-6 flex gap-2 border-t border-border bg-card px-6 py-4">
									<Button
										variant="outline"
										className="flex-1 rounded-sm"
										onClick={() => decide(false)}
										disabled={deciding}
									>
										<X className="mr-2 h-4 w-4" />
										Rifiuta
									</Button>
									<Button
										className="flex-1 rounded-sm bg-volt hover:bg-volt/90"
										onClick={() => decide(true)}
										disabled={deciding || !isCoach}
									>
										{deciding ? (
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
										) : (
											<Check className="mr-2 h-4 w-4" />
										)}
										Consenti
									</Button>
								</div>
							</>
						)}
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
